import * as Yup from "yup";
import { workflowService } from "@/services/breeze/WorkflowService";
import {
  CreateUpdateWorkflow,
  WorkflowEvent,
  WorkflowTriggerType,
} from "@/types/breeze/Workflow";
import InputField from "@/components/form/InputField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PageTemplate from "@/components/page-template/PageTemplate";
import WorkflowSettings from "@/components/workflow/WorkflowSettings";
import { useAuth } from "@/providers/AuthProvider";
import { Flex } from "@radix-ui/themes";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  description: Yup.string(),
  group: Yup.string().nullable().max(120, "Group must be 120 characters or fewer"),
});

const initialValues: CreateUpdateWorkflow = {
  name: "",
  description: undefined,
  group: undefined,
  enabled: false,
  editor_state: "",
  triggers: [
    {
      type: WorkflowTriggerType.Event,
      enabled: true,
      config: {
        event: WorkflowEvent.None,
        event_entity: "",
        manual_entities: [],
        cron_expression: "",
        api_route: "",
      },
    },
  ],
};

const CreateWorkflowPage = () => {
  const { currentTenant, safeNavigate } = useAuth();
  const queryClient = useQueryClient();

  const { mutate: createWorkflow, isPending: isCreatingWorkflow } = useMutation(
    {
      mutationFn: (values: CreateUpdateWorkflow) =>
        workflowService.createWorkflow(values),
      onSuccess: (workflow) => {
        queryClient.invalidateQueries({ queryKey: ["workflows"] });
        safeNavigate(`/org/${currentTenant?.id}/workflows/${workflow.id}`);
      },
      onError: (error) => {
        console.error("Failed to create workflow:", error);
      },
    },
  );

  return (
    <Flex p="5" direction="column" gap="4">
      <PageTemplate
        title="Create New Workflow"
        description="Create a new workflow to automate your business processes"
      >
        <WorkflowSettings<CreateUpdateWorkflow>
          workflow={initialValues}
          isSubmitting={isCreatingWorkflow}
          extraValidationSchema={validationSchema}
          extraFields={
            <>
              <InputField
                name="name"
                label="Name"
                required
                description="Give your workflow a unique name, e.g., 'New User Registration Notification'"
              />

              <InputField
                name="description"
                label="Description"
                as="textarea"
                options={{ rows: 3 }}
                description="Explain what automation triggers and steps this workflow performs"
              />

              <InputField
                name="group"
                label="Group"
                description="Optional. Group related workflows together (e.g. 'Business logic', 'Comms') so you can filter them on the list page."
              />
            </>
          }
          saveButtonText="Create Workflow"
          showCancelButton={false}
          onSave={(values) => createWorkflow(values)}
        />
      </PageTemplate>
    </Flex>
  );
};

export default CreateWorkflowPage;
